import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, ChevronLeft, ExternalLink, FileText, List, MapPin, Mic, Plus, Play, Trash2, UserRound,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { apiFetch, makeClient } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import {
  META_LIMITS, buttonsMenu, listMenu, metaNotes, parseBody as parse, runes, str,
  type Body, type Btn, type Media, type Row, type Section, type TesterType,
} from '@/lib/message-tester';

// Testador por tipo do trilho oficial: um formulário para cada tipo de mensagem e a
// prévia num celular, ao lado. O formulário lê e escreve o MESMO corpo da
// requisição (payload) — o JSON e o snippet continuam mostrando exatamente o que
// sai. A prévia avisa o que a Meta faria com a mensagem antes de gastar o envio.

// ----------------------------------------------------------------------------
// Formulário por tipo

function Field({ label, count, max, children, hint }: { label: string; count?: number; max?: number; children: React.ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Label>{label}</Label>
        {max != null && <span className={cn('text-xs tabular-nums', (count ?? 0) > max ? 'text-destructive' : 'text-muted-foreground')}>{count ?? 0}/{max}</span>}
      </div>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const areaCls = 'w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40';
const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

/** A última mensagem que o contato mandou (wamid) — o alvo natural de uma reação. */
function useLastInbound(phone: string) {
  const digits = phone.replace(/\D/g, '');
  const { data: convs = [] } = useQuery({
    queryKey: ['conversations', ''],
    enabled: digits.length >= 8,
    queryFn: async () => ((await apiFetch('GET', '/conversations')).data as { data?: { chat_jid: string }[] })?.data ?? [],
  });
  const jid = convs.find((c) => c.chat_jid.split('@')[0] === digits)?.chat_jid;
  const { data } = useQuery({
    queryKey: ['history', jid],
    enabled: !!jid,
    queryFn: async () => {
      const r = await makeClient().GET('/conversations/{jid}/messages', {
        params: { path: { jid: jid! }, query: {} as { instance_id: string } },
      });
      return ((r.data as { data?: { wa_message_id?: string; direction: string; created_at: string; type: string; payload?: { body?: string; caption?: string } }[] })?.data ?? []);
    },
  });
  return useMemo(() => (data ?? [])
    .filter((m) => m.direction === 'in' && m.wa_message_id)
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .pop(), [data]);
}

export function MessageTypeForm({ type, payload, onPayload }: { type: TesterType; payload: string; onPayload: (p: string) => void }) {
  const t = useT();
  const body = parse(payload);
  const media = (body.media ?? {}) as Media;
  const write = (next: Body) => onPayload(JSON.stringify(next, null, 2));
  const set = (k: string, v: unknown) => {
    const next = { ...body };
    if (v === '' || v === undefined) delete next[k]; else next[k] = v;
    write(next);
  };
  const setMedia = (k: keyof Media, v: string) => {
    const m: Media = { ...media };
    if (v === '') delete m[k]; else (m as Record<string, unknown>)[k] = v;
    write({ ...body, media: m });
  };
  const last = useLastInbound(type === 'reaction' ? str(body.to) : '');
  const f = (k: string) => t(`playground.tester.field.${k}`);
  // Cabeçalho de texto (botões, lista, botão de link). Mídia no cabeçalho vai pelo JSON.
  const header = (body.header ?? {}) as { type?: string; text?: string };
  const headerField = (
    <Field label={f('header')} count={runes(header.text)} max={META_LIMITS.header}>
      <Input value={header.type === 'text' || !header.type ? str(header.text) : ''} disabled={!!header.type && header.type !== 'text'}
        onChange={(e) => set('header', e.target.value === '' ? '' : { type: 'text', text: e.target.value })} className="h-9" />
    </Field>
  );

  switch (type) {
    case 'text':
      return (
        <>
          <Field label={f('text')} count={runes(body.body)} max={META_LIMITS.text}>
            <textarea rows={3} value={str(body.body)} onChange={(e) => set('body', e.target.value)} className={areaCls} />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={body.preview_url === true} onChange={(e) => set('preview_url', e.target.checked ? true : '')} />
            {f('previewUrl')}
          </label>
        </>
      );
    case 'ctaUrl':
      return (
        <>
          {headerField}
          <Field label={f('body')} count={runes(body.body)} max={META_LIMITS.body}>
            <textarea rows={2} value={str(body.body)} onChange={(e) => set('body', e.target.value)} className={areaCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={f('buttonText')} count={runes(body.button_text)} max={META_LIMITS.ctaButton}>
              <Input value={str(body.button_text)} onChange={(e) => set('button_text', e.target.value)} className="h-9" />
            </Field>
            <Field label={f('footer')}><Input value={str(body.footer)} onChange={(e) => set('footer', e.target.value)} className="h-9" /></Field>
          </div>
          <Field label={f('link')}>
            <Input value={str(body.url)} onChange={(e) => set('url', e.target.value)} className="h-9 font-mono text-xs" placeholder="https://" />
          </Field>
        </>
      );
    case 'locationRequest':
      return (
        <Field label={f('body')} count={runes(body.body)} max={META_LIMITS.body}>
          <textarea rows={2} value={str(body.body)} onChange={(e) => set('body', e.target.value)} className={areaCls} />
        </Field>
      );
    case 'image': case 'video': case 'document': case 'audio': case 'sticker':
      return (
        <>
          <Field label={f('url')} hint={t(`playground.tester.mediaHint.${type}`)}>
            <Input value={str(media.url)} onChange={(e) => setMedia('url', e.target.value)} className="h-9 font-mono text-xs" placeholder="https://" />
          </Field>
          {type === 'document' && (
            <Field label={f('filename')}>
              <Input value={str(media.filename)} onChange={(e) => setMedia('filename', e.target.value)} className="h-9" />
            </Field>
          )}
          {(type === 'image' || type === 'video' || type === 'document') && (
            <Field label={f('caption')} count={runes(media.caption)} max={META_LIMITS.caption}>
              <textarea rows={2} value={str(media.caption)} onChange={(e) => setMedia('caption', e.target.value)} className={areaCls} />
            </Field>
          )}
        </>
      );
    case 'location':
      return (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label={f('latitude')}>
              <Input type="number" step="any" value={str(body.latitude)} onChange={(e) => set('latitude', e.target.value === '' ? '' : Number(e.target.value))} className="h-9 font-mono text-xs" />
            </Field>
            <Field label={f('longitude')}>
              <Input type="number" step="any" value={str(body.longitude)} onChange={(e) => set('longitude', e.target.value === '' ? '' : Number(e.target.value))} className="h-9 font-mono text-xs" />
            </Field>
          </div>
          <Field label={f('placeName')}><Input value={str(body.name)} onChange={(e) => set('name', e.target.value)} className="h-9" /></Field>
          <Field label={f('address')}><Input value={str(body.address)} onChange={(e) => set('address', e.target.value)} className="h-9" /></Field>
        </>
      );
    case 'contact':
      return (
        <>
          <Field label={f('contactName')}><Input value={str(body.contact_name)} onChange={(e) => set('contact_name', e.target.value)} className="h-9" /></Field>
          <Field label={f('contactPhone')} hint={t('playground.tester.contactHint')}>
            <Input value={str(body.contact_phone)} onChange={(e) => set('contact_phone', e.target.value)} className="h-9 font-mono text-xs" placeholder="+5511977776666" />
          </Field>
        </>
      );
    case 'reaction':
      return (
        <>
          <Field label={f('emoji')}>
            <div className="flex flex-wrap items-center gap-1.5">
              {EMOJIS.map((e) => (
                <button key={e} type="button" onClick={() => set('emoji', e)}
                  className={cn('rounded-md border px-2 py-1 text-lg leading-none', body.emoji === e ? 'border-primary bg-primary/10' : 'border-input hover:bg-muted')}>{e}</button>
              ))}
              <Input value={str(body.emoji)} onChange={(e) => write({ ...body, emoji: e.target.value })} className="h-9 w-20 text-center" />
            </div>
          </Field>
          <Field label={f('messageId')} hint={t('playground.tester.reactionHint')}>
            <Input value={str(body.quoted_message_id)} onChange={(e) => set('quoted_message_id', e.target.value)} className="h-9 font-mono text-xs" placeholder="wamid.…" />
          </Field>
          {last?.wa_message_id && last.wa_message_id !== body.quoted_message_id && (
            <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => set('quoted_message_id', last.wa_message_id)}>
              {t('playground.tester.useLast')}
              {(last.payload?.body || last.payload?.caption) && <span className="max-w-48 truncate text-muted-foreground">“{last.payload?.body || last.payload?.caption}”</span>}
            </Button>
          )}
        </>
      );
    case 'buttons': {
      const bs = (body.buttons ?? []) as Btn[];
      const setBtn = (i: number, title: string) => write({ ...body, buttons: bs.map((b, j) => (j === i ? { ...b, title } : b)) });
      return (
        <>
          {headerField}
          <Field label={f('body')} count={runes(body.body)} max={META_LIMITS.body}>
            <textarea rows={2} value={str(body.body)} onChange={(e) => set('body', e.target.value)} className={areaCls} />
          </Field>
          <Field label={f('footer')}><Input value={str(body.footer)} onChange={(e) => set('footer', e.target.value)} className="h-9" /></Field>
          <Field label={t('playground.tester.buttonsLabel', { max: META_LIMITS.buttons })}>
            <div className="flex flex-col gap-2">
              {bs.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={str(b.title)} onChange={(e) => setBtn(i, e.target.value)} className="h-9" />
                  <span className={cn('w-12 shrink-0 text-right text-xs tabular-nums', runes(b.title) > META_LIMITS.buttonTitle ? 'text-destructive' : 'text-muted-foreground')}>{runes(b.title)}/{META_LIMITS.buttonTitle}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => write({ ...body, buttons: bs.filter((_, j) => j !== i) })} aria-label={t('playground.tester.remove')}><Trash2 className="size-4" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="self-start"
                onClick={() => write({ ...body, buttons: [...bs, { id: `btn_${bs.length + 1}`, title: '' }] })}>
                <Plus className="size-4" /> {t('playground.tester.addButton')}
              </Button>
            </div>
          </Field>
        </>
      );
    }
    case 'list': {
      const sections = (body.sections ?? []) as Section[];
      const sec = sections[0] ?? { title: '', rows: [] };
      const rows = sec.rows ?? [];
      const writeRows = (next: Row[]) => write({ ...body, sections: [{ ...sec, rows: next }, ...sections.slice(1)] });
      const setRow = (i: number, k: keyof Row, v: string) => writeRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
      return (
        <>
          {headerField}
          <Field label={f('body')} count={runes(body.body)} max={META_LIMITS.body}>
            <textarea rows={2} value={str(body.body)} onChange={(e) => set('body', e.target.value)} className={areaCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={f('listButton')} count={runes(body.button_text)} max={META_LIMITS.listButton}>
              <Input value={str(body.button_text)} onChange={(e) => set('button_text', e.target.value)} className="h-9" />
            </Field>
            <Field label={f('footer')}><Input value={str(body.footer)} onChange={(e) => set('footer', e.target.value)} className="h-9" /></Field>
          </div>
          <Field label={t('playground.tester.rowsLabel', { max: META_LIMITS.listRows })}>
            <div className="flex flex-col gap-2">
              {rows.map((r, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="grid flex-1 gap-1.5 sm:grid-cols-2">
                    <Input value={str(r.title)} onChange={(e) => setRow(i, 'title', e.target.value)} placeholder={f('rowTitle')}
                      className={cn('h-9', runes(r.title) > META_LIMITS.rowTitle && 'border-destructive')} />
                    <Input value={str(r.description)} onChange={(e) => setRow(i, 'description', e.target.value)} placeholder={f('rowDesc')}
                      className={cn('h-9', runes(r.description) > META_LIMITS.rowDesc && 'border-destructive')} />
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => writeRows(rows.filter((_, j) => j !== i))} aria-label={t('playground.tester.remove')}><Trash2 className="size-4" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="self-start"
                onClick={() => writeRows([...rows, { id: `row_${rows.length + 1}`, title: '' }])}>
                <Plus className="size-4" /> {t('playground.tester.addRow')}
              </Button>
            </div>
          </Field>
        </>
      );
    }
  }
}

// ----------------------------------------------------------------------------
// Prévia no celular

/** Moldura de celular com o cabeçalho da conversa: o número do projeto falando. */
export function PhoneFrame({ name, children, footer }: { name?: string; children: React.ReactNode; footer?: React.ReactNode }) {
  const t = useT();
  return (
    <div className="mx-auto w-full max-w-[300px] overflow-hidden rounded-[2rem] border-[6px] border-[#1f2c34] bg-[#1f2c34] shadow-lg">
      <div className="flex items-center gap-2 bg-[#008069] px-3 py-2 text-white dark:bg-[#202c33]">
        <ChevronLeft className="size-4 shrink-0" />
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white/25 text-xs font-semibold">{(name || 'B').slice(0, 1).toUpperCase()}</div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium leading-tight">{name || t('playground.tester.yourNumber')}</p>
          <p className="text-[10px] leading-tight text-white/75">{t('playground.tester.business')}</p>
        </div>
      </div>
      <div className="flex min-h-64 flex-col justify-end gap-1 bg-[#efeae2] p-3 dark:bg-[#0b141a]">
        {children}
      </div>
      {footer}
    </div>
  );
}

const bubbleCls = 'max-w-[85%] self-start rounded-lg rounded-tl-none bg-white px-2.5 py-1.5 text-[13px] leading-snug text-[#111b21] shadow-sm dark:bg-[#202c33] dark:text-[#e9edef]';
const metaCls = 'mt-0.5 block text-right text-[10px] text-black/45 dark:text-white/45';

function Time() {
  const now = new Date();
  return <span className={metaCls}>{now.getHours().toString().padStart(2, '0')}:{now.getMinutes().toString().padStart(2, '0')}</span>;
}

/** Botões de resposta como o WhatsApp desenha: faixas sob o balão. */
function ActionRow({ children }: { children: React.ReactNode }) {
  return <div className="rounded-md bg-white py-1.5 text-center text-[13px] font-medium text-[#027eb5] shadow-sm dark:bg-[#202c33] dark:text-[#53bdeb]">{children}</div>;
}

function MediaBox({ url, kind }: { url?: string; kind: 'image' | 'video' | 'sticker' }) {
  if (!url) return <div className="flex h-32 items-center justify-center rounded bg-black/5 text-xs text-black/40 dark:bg-white/5 dark:text-white/40">URL</div>;
  if (kind === 'video') {
    return (
      <div className="relative flex h-36 items-center justify-center overflow-hidden rounded bg-black">
        <video src={url} muted preload="metadata" className="absolute inset-0 size-full object-cover opacity-80" />
        <span className="relative flex size-10 items-center justify-center rounded-full bg-black/50"><Play className="size-5 fill-white text-white" /></span>
      </div>
    );
  }
  return <img src={url} alt="" className={kind === 'sticker' ? 'size-32 object-contain' : 'max-h-48 w-full rounded object-cover'} />;
}

/** Cabeçalho de texto do interativo, em negrito no topo do balão. */
function HeaderLine({ body }: { body: Body }) {
  const h = (body.header ?? {}) as { type?: string; text?: string };
  if (h.type !== 'text' || !h.text) return null;
  return <p className="mb-0.5 font-semibold">{h.text}</p>;
}

export function MessagePreview({ type, body }: { type: TesterType; body: Body }) {
  const t = useT();
  const media = (body.media ?? {}) as Media;
  const notes = metaNotes(type, body);
  switch (type) {
    case 'text':
      return <div className={bubbleCls}><p className="whitespace-pre-wrap break-words">{str(body.body) || '…'}</p><Time /></div>;
    case 'image': case 'video':
      return (
        <div className={cn(bubbleCls, 'p-1')}>
          <MediaBox url={media.url} kind={type} />
          {media.caption && <p className="whitespace-pre-wrap break-words px-1.5 pt-1">{media.caption}</p>}
          <span className="px-1.5"><Time /></span>
        </div>
      );
    case 'sticker':
      return <div className="self-start"><MediaBox url={media.url} kind="sticker" /></div>;
    case 'document': {
      const name = media.filename || str(media.url).split('/').pop() || 'documento';
      return (
        <div className={cn(bubbleCls, 'p-1')}>
          <div className="flex items-center gap-2 rounded bg-black/5 px-2 py-2.5 dark:bg-white/5">
            <FileText className="size-7 shrink-0 text-[#e5534b]" />
            <span className="min-w-0 truncate text-[13px]">{name}</span>
          </div>
          {media.caption && <p className="whitespace-pre-wrap break-words px-1.5 pt-1">{media.caption}</p>}
          <span className="px-1.5"><Time /></span>
        </div>
      );
    }
    case 'audio':
      return (
        <div className={cn(bubbleCls, 'flex min-w-48 items-center gap-2')}>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#00a884] text-white"><Mic className="size-4" /></span>
          <div className="h-1 flex-1 rounded bg-black/15 dark:bg-white/20" />
          <Time />
        </div>
      );
    case 'location':
      return (
        <div className={cn(bubbleCls, 'p-1')}>
          <div className="flex h-24 items-center justify-center rounded bg-[#dfe8d2] dark:bg-[#2a3a2f]"><MapPin className="size-8 fill-[#e5534b] text-[#b0302a]" /></div>
          {(body.name || body.address) ? (
            <div className="px-1.5 pt-1">
              {body.name ? <p className="font-medium">{str(body.name)}</p> : null}
              {body.address ? <p className="text-[12px] text-black/55 dark:text-white/55">{str(body.address)}</p> : null}
            </div>
          ) : null}
          <span className="px-1.5"><Time /></span>
        </div>
      );
    case 'contact':
      return (
        <div className={cn(bubbleCls, 'min-w-48 p-0')}>
          <div className="flex items-center gap-2 px-2.5 py-2">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-black/10 dark:bg-white/10"><UserRound className="size-5" /></span>
            <span className="truncate font-medium">{str(body.contact_name) || '…'}</span>
          </div>
          <p className="border-t border-black/10 py-1.5 text-center text-[13px] font-medium text-[#027eb5] dark:border-white/10 dark:text-[#53bdeb]">{t('playground.tester.message')}</p>
        </div>
      );
    case 'reaction':
      return (
        <div className="flex flex-col items-end self-end">
          <div className="max-w-[85%] rounded-lg rounded-tr-none bg-[#d9fdd3] px-2.5 py-1.5 text-[13px] text-[#111b21] shadow-sm dark:bg-[#005c4b] dark:text-[#e9edef]">
            {t('playground.tester.contactMessage')}
          </div>
          <span className="-mt-1.5 mr-2 rounded-full border border-white bg-white px-1 text-sm shadow-sm dark:border-[#0b141a] dark:bg-[#202c33]">{str(body.emoji) || '·'}</span>
        </div>
      );
    case 'buttons': {
      const bs = (body.buttons ?? []) as Btn[];
      if (notes.includes('buttonsAsMenu')) {
        return <div className={bubbleCls}><p className="whitespace-pre-wrap break-words">{buttonsMenu(body, bs)}</p><Time /></div>;
      }
      return (
        <div className="flex w-[85%] flex-col gap-px self-start">
          <div className={cn(bubbleCls, 'max-w-none')}>
            <HeaderLine body={body} />
            <p className="whitespace-pre-wrap break-words">{str(body.body) || '…'}</p>
            {body.footer ? <p className="text-[11px] text-black/50 dark:text-white/50">{str(body.footer)}</p> : null}
            <Time />
          </div>
          {bs.map((b, i) => <ActionRow key={i}>{str(b.title)}</ActionRow>)}
        </div>
      );
    }
    case 'list': {
      if (notes.includes('listAsMenu')) {
        return <div className={bubbleCls}><p className="whitespace-pre-wrap break-words">{listMenu(body, (body.sections ?? []) as Section[])}</p><Time /></div>;
      }
      return (
        <div className="flex w-[85%] flex-col gap-px self-start">
          <div className={cn(bubbleCls, 'max-w-none')}>
            <HeaderLine body={body} />
            <p className="whitespace-pre-wrap break-words">{str(body.body) || '…'}</p>
            {body.footer ? <p className="text-[11px] text-black/50 dark:text-white/50">{str(body.footer)}</p> : null}
            <Time />
          </div>
          <ActionRow><span className="inline-flex items-center gap-1.5"><List className="size-4" />{str(body.button_text) || '…'}</span></ActionRow>
        </div>
      );
    }
    case 'ctaUrl':
      return (
        <div className="flex w-[85%] flex-col gap-px self-start">
          <div className={cn(bubbleCls, 'max-w-none')}>
            <HeaderLine body={body} />
            <p className="whitespace-pre-wrap break-words">{str(body.body) || '…'}</p>
            {body.footer ? <p className="text-[11px] text-black/50 dark:text-white/50">{str(body.footer)}</p> : null}
            <Time />
          </div>
          <ActionRow><span className="inline-flex items-center gap-1.5"><ExternalLink className="size-4" />{str(body.button_text) || '…'}</span></ActionRow>
        </div>
      );
    case 'locationRequest':
      return (
        <div className="flex w-[85%] flex-col gap-px self-start">
          <div className={cn(bubbleCls, 'max-w-none')}>
            <p className="whitespace-pre-wrap break-words">{str(body.body) || '…'}</p>
            <Time />
          </div>
          <ActionRow><span className="inline-flex items-center gap-1.5"><MapPin className="size-4" />{t('playground.tester.sendLocation')}</span></ActionRow>
        </div>
      );
  }
}

/** Avisos do que a Meta faria com a mensagem, sob a prévia. */
export function MetaNotes({ notes }: { notes: string[] }) {
  const t = useT();
  if (notes.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1.5">
      {notes.map((n) => (
        <li key={n} className="flex items-start gap-1.5 text-xs text-[#b5620a]">
          <AlertTriangle className="mt-px size-3.5 shrink-0" /> {t(`playground.tester.note.${n}`)}
        </li>
      ))}
    </ul>
  );
}

/** Formulário + prévia lado a lado. */
export function PlaygroundTester({ type, payload, onPayload, businessName }: {
  type: TesterType; payload: string; onPayload: (p: string) => void; businessName?: string;
}) {
  const t = useT();
  const body = parse(payload);
  return (
    <div className="flex flex-wrap gap-5 rounded-md border bg-muted/30 p-4">
      <div className="flex min-w-64 flex-1 flex-col gap-3">
        <MessageTypeForm type={type} payload={payload} onPayload={onPayload} />
      </div>
      <div className="flex w-full flex-col gap-2 sm:w-[300px] sm:shrink-0">
        <Label>{t('playground.template.preview')}</Label>
        <PhoneFrame name={businessName}><MessagePreview type={type} body={body} /></PhoneFrame>
        <MetaNotes notes={metaNotes(type, body)} />
      </div>
    </div>
  );
}
